import { useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ApproversInfo } from "@/shared/components/ApproversInfo";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Field, FieldLabel } from "@/components/ui/field";
import { useNotification } from "@/shared/hooks/useNotification";
import { getErrorMessage } from "@/shared/utils/errorHandler";
import { LoadingAnimation } from "@/shared/components/LoadingAnimation";
import { TablePagination } from "@/shared/components/TablePagination";
import {
  usePendingSetupApprovals,
  useApproveSetupApproval,
  useRejectSetupApproval,
} from "../hooks/useSetupApprovals";
import type { SetupApprovalRequest } from "../services/setupApprovalApi";

const formatDate = (value?: string) => {
  if (!value) return "—";
  return new Date(value).toLocaleDateString("en-KE", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const TypeBadge = ({ type }: { type: string }) => {
  const colours: Record<string, string> = {
    Pricing: "bg-blue-100 text-blue-800",
    CropType: "bg-green-100 text-green-800",
    CropVariety: "bg-purple-100 text-purple-800",
  };
  return (
    <span
      className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${
        colours[type] ?? "bg-gray-100 text-gray-800"
      }`}
    >
      {type === "CropType" ? "Crop Type" : type === "CropVariety" ? "Crop Variety" : type}
    </span>
  );
};

// ── Approve Modal ─────────────────────────────────────────────────────────────

const ApproveModal = ({
  open,
  onOpenChange,
  onConfirm,
  isSubmitting,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  onConfirm: (notes?: string) => void;
  isSubmitting: boolean;
}) => {
  const [notes, setNotes] = useState("");
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Approve Setup Change</DialogTitle>
          <DialogDescription>
            The proposed change will be applied to live data immediately.
          </DialogDescription>
        </DialogHeader>
        <Field>
          <FieldLabel>Review Notes (optional)</FieldLabel>
          <Textarea
            placeholder="Any comments for the maker..."
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            disabled={isSubmitting}
          />
        </Field>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button onClick={() => onConfirm(notes || undefined)} disabled={isSubmitting}>
            {isSubmitting ? "Approving..." : "Approve"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ── Reject Modal ──────────────────────────────────────────────────────────────

const RejectModal = ({
  open,
  onOpenChange,
  onConfirm,
  isSubmitting,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  onConfirm: (reason: string) => void;
  isSubmitting: boolean;
}) => {
  const [reason, setReason] = useState("");
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Reject Setup Change</DialogTitle>
          <DialogDescription>
            The maker will be notified of the rejection reason.
          </DialogDescription>
        </DialogHeader>
        <Field>
          <FieldLabel>
            Rejection Reason <span className="text-red-500">*</span>
          </FieldLabel>
          <Textarea
            placeholder="Explain why this change is being rejected..."
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            disabled={isSubmitting}
          />
        </Field>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={() => onConfirm(reason)}
            disabled={isSubmitting || !reason.trim()}
          >
            {isSubmitting ? "Rejecting..." : "Reject"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ── Detail Panel ──────────────────────────────────────────────────────────────

const formatKey = (k: string) => k.replace(/([A-Z])/g, " $1").trim();

const formatValue = (v: unknown): string => {
  if (v === null || v === undefined) return "—";
  if (typeof v === "boolean") return v ? "Yes" : "No";
  if (typeof v === "object") return JSON.stringify(v, null, 2);
  return String(v);
};

const ObjectFields = ({ obj }: { obj: Record<string, unknown> }) => (
  <dl className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
    {Object.entries(obj)
      .filter(([, v]) => v !== null && v !== undefined && v !== "")
      .map(([k, v]) => (
        <div key={k} className="contents">
          <dt className="text-muted-foreground capitalize">{formatKey(k)}</dt>
          <dd className="font-medium whitespace-pre-wrap">{formatValue(v)}</dd>
        </div>
      ))}
  </dl>
);

const JsonPreview = ({ json }: { json: string }) => {
  let parsed: unknown;
  try {
    parsed = JSON.parse(json);
  } catch {
    return <p className="text-xs text-muted-foreground">{json}</p>;
  }

  if (Array.isArray(parsed)) {
    return (
      <div className="space-y-3">
        {parsed.map((item, i) => (
          <div key={i} className="rounded-md border border-muted p-3">
            <p className="text-xs font-semibold text-muted-foreground mb-2">Entry {i + 1}</p>
            {typeof item === "object" && item !== null ? (
              <ObjectFields obj={item as Record<string, unknown>} />
            ) : (
              <p className="text-xs">{String(item)}</p>
            )}
          </div>
        ))}
      </div>
    );
  }

  if (typeof parsed === "object" && parsed !== null) {
    return <ObjectFields obj={parsed as Record<string, unknown>} />;
  }

  return <p className="text-xs text-muted-foreground">{String(parsed)}</p>;
};

// ── Page ──────────────────────────────────────────────────────────────────────

export const SetupApprovalsPage = () => {
  const { data: requests, isLoading, error } = usePendingSetupApprovals();
  const approveMutation = useApproveSetupApproval();
  const rejectMutation = useRejectSetupApproval();
  const notify = useNotification();

  const [approving, setApproving] = useState<SetupApprovalRequest | null>(null);
  const [rejecting, setRejecting] = useState<SetupApprovalRequest | null>(null);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  // Client-side pagination
  const totalCount = requests?.length ?? 0;
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
  const paged = (requests ?? []).slice((page - 1) * pageSize, page * pageSize);

  const handleApprove = (notes?: string) => {
    if (!approving) return;
    approveMutation.mutate(
      { id: approving.id, payload: { reviewNotes: notes } },
      {
        onSuccess: () => {
          notify.success("Setup change approved and applied");
          setApproving(null);
        },
        onError: (err) => notify.error(getErrorMessage(err)),
      }
    );
  };

  const handleReject = (reason: string) => {
    if (!rejecting) return;
    rejectMutation.mutate(
      { id: rejecting.id, payload: { rejectionReason: reason } },
      {
        onSuccess: () => {
          notify.success("Setup change rejected");
          setRejecting(null);
        },
        onError: (err) => notify.error(getErrorMessage(err)),
      }
    );
  };

  if (isLoading) return <LoadingAnimation fullScreen />;

  if (error) {
    return (
      <div className="rounded-lg border border-red-200 bg-red-50 p-4">
        <p className="text-red-600">Failed to load pending setup approvals</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Setup Approvals</h1>
        <p className="text-muted-foreground mt-1">
          Review and approve pending changes to inputs, input types, crop types, crop varieties, and pricing
        </p>
        <div className="mt-2">
          <ApproversInfo permission="crop_types.approve" />
        </div>
      </div>

      {(!requests || requests.length === 0) ? (
        <div className="rounded-lg border bg-muted/30 py-16 text-center">
          <p className="text-muted-foreground">No pending setup changes</p>
        </div>
      ) : (
        <>
        <div className="rounded-lg border overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Type</TableHead>
                <TableHead>Description</TableHead>
                <TableHead>Submitted By</TableHead>
                <TableHead>Submitted At</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {paged.map((req) => (
                <>
                  <TableRow
                    key={req.id}
                    className="cursor-pointer hover:bg-muted/40"
                    onClick={() => setExpanded(expanded === req.id ? null : req.id)}
                  >
                    <TableCell>
                      <TypeBadge type={req.approvalTypeName} />
                    </TableCell>
                    <TableCell className="font-medium">{req.description}</TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {req.requestedByUserName}
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {formatDate(req.requestedAt)}
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className="text-amber-600 border-amber-300">
                        Pending
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <Button
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            setApproving(req);
                          }}
                        >
                          Approve
                        </Button>
                        <Button
                          size="sm"
                          variant="destructive"
                          onClick={(e) => {
                            e.stopPropagation();
                            setRejecting(req);
                          }}
                        >
                          Reject
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>

                  {expanded === req.id && (
                    <TableRow key={`${req.id}-detail`}>
                      <TableCell colSpan={6} className="bg-muted/20 px-6 py-4">
                        <p className="text-xs font-semibold text-muted-foreground mb-2 uppercase tracking-wide">
                          Proposed {req.existingEntityId ? "Update" : "New Record"}
                        </p>
                        <JsonPreview json={req.entityJson} />
                      </TableCell>
                    </TableRow>
                  )}
                </>
              ))}
            </TableBody>
          </Table>
        </div>

        {totalCount > pageSize && (
          <TablePagination
            pageNumber={page}
            pageSize={pageSize}
            totalCount={totalCount}
            totalPages={totalPages}
            onPageChange={setPage}
            onPageSizeChange={(size) => { setPageSize(size); setPage(1); }}
          />
        )}
        </>
      )}

      <ApproveModal
        open={!!approving}
        onOpenChange={(v) => !v && setApproving(null)}
        onConfirm={handleApprove}
        isSubmitting={approveMutation.isPending}
      />

      <RejectModal
        open={!!rejecting}
        onOpenChange={(v) => !v && setRejecting(null)}
        onConfirm={handleReject}
        isSubmitting={rejectMutation.isPending}
      />
    </div>
  );
};
